import {
  collection, doc, getDoc, getDocs, limit, query, runTransaction, serverTimestamp, setDoc, where,
} from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { getPublicPlatformRuntime, sendTransactionalEmail } from './backend';
import { uploadFile, validateUploadFile } from './storage';
import { paymentReceiptAttachment, paymentReceiptEmailHtml, resolveReceiptConfig, receiptNumberFor } from '../../utils/paymentReceipt';

const proofTypes = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];
const normalizeReference = (value) => String(value || '').trim().replace(/\s+/g, ' ');
const normalizeMethod = (value) => String(value || '').trim().toLowerCase();
const hash = (value) => {
  let h = 2166136261;
  for (const ch of String(value || '')) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619); }
  return (h >>> 0).toString(36);
};

export async function getManualPaymentOptions() {
  const runtime = await getPublicPlatformRuntime();
  const payments = runtime?.payments || {};
  const manual = payments.manual || {};
  const paymentEnabled = payments.enabled !== false;
  return {
    paymentEnabled,
    currency: payments.currency || 'EUR',
    online: {
      enabled: paymentEnabled && payments.online?.enabled !== false && Boolean(payments.provider),
      provider: payments.provider || 'stripe',
    },
    upi: { ...(manual.upi || {}), enabled: paymentEnabled && Boolean(manual.upi?.enabled) },
    bankTransfer: { ...(manual.bankTransfer || {}), enabled: paymentEnabled && Boolean(manual.bankTransfer?.enabled) },
    approvalRequired: manual.approvalRequired !== false,
    receipt: resolveReceiptConfig(runtime),
  };
}

async function expectedPlanDetails(packageId, fallback = {}) {
  if (!packageId) {
    return {
      amount: Number(fallback.amount || 0),
      currency: String(fallback.currency || 'EUR').toUpperCase(),
      billingCycle: String(fallback.billingCycle || ''),
      packageName: String(fallback.packageName || ''),
    };
  }
  const snap = await getDoc(doc(db, 'subscriptionPlans', packageId));
  if (!snap.exists()) throw new Error('The selected subscription plan is no longer available.');
  const plan = snap.data() || {};
  if (plan.isActive === false) throw new Error('The selected subscription plan is no longer active.');
  return {
    amount: Number(plan.price || 0),
    currency: String(plan.currency || fallback.currency || 'EUR').toUpperCase(),
    billingCycle: String(plan.billingCycle || fallback.billingCycle || ''),
    packageName: String(plan.name || fallback.packageName || ''),
  };
}

export async function submitManualPayment({
  companyId, userId, paymentType = 'company_registration', packageId = '', packageName = '',
  amount = 0, currency = 'EUR', paymentMethod, transactionReference, paymentDate,
  proofFile, notes = '', companyName = '', userName = '', email = '', billingCycle = '', periodEnd = '',
}) {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser?.uid) throw new Error('Please sign in before submitting payment proof.');
  if (!companyId) throw new Error('Company information is missing. Refresh the page and try again.');
  if (userId && String(userId) !== String(firebaseUser.uid)) throw new Error('You can only submit payment proof for your own registration.');

  const options = await getManualPaymentOptions();
  if (!options.paymentEnabled) throw new Error('Payments are currently disabled by Super Admin.');
  const method = normalizeMethod(paymentMethod);
  if (method === 'upi' && !options.upi.enabled) throw new Error('UPI payment is currently disabled by Super Admin.');
  if (method === 'bank_transfer' && !options.bankTransfer.enabled) throw new Error('Bank transfer is currently disabled by Super Admin.');
  if (!['upi', 'bank_transfer'].includes(method)) throw new Error('Choose UPI or Bank Transfer.');

  const reference = normalizeReference(transactionReference);
  if (reference.length < 4) throw new Error('Enter a valid transaction/UTR/reference number.');
  if (!proofFile) throw new Error('Upload the payment screenshot or bank receipt.');
  validateUploadFile(proofFile, { allowedTypes: proofTypes, maxBytes: 10 * 1024 * 1024 });

  const verifiedPlan = await expectedPlanDetails(packageId, { amount, currency, billingCycle, packageName });
  const expectedAmount = Number(verifiedPlan.amount || 0);
  if (!(expectedAmount > 0)) throw new Error('The payable amount could not be verified from the selected plan.');

  const paymentId = `manual_${hash(`${companyId}:${method}:${reference.toLowerCase()}`)}`;
  const paymentRef = doc(db, 'paymentProofs', paymentId);
  const existing = await getDoc(paymentRef);
  const existingData = existing.exists() ? existing.data() || {} : null;
  const canResubmit = Boolean(
    existingData &&
    String(existingData.status || '').toLowerCase() === 'rejected' &&
    String(existingData.companyId || '') === String(companyId) &&
    String(existingData.userId || '') === String(firebaseUser.uid)
  );
  if (existing.exists() && !canResubmit) throw new Error('This transaction/reference number has already been submitted.');

  const companySnap = await getDoc(doc(db, 'companies', companyId));
  if (!companySnap.exists()) throw new Error('Company record was not found.');
  const company = companySnap.data() || {};
  const profileSnap = await getDoc(doc(db, 'users', firebaseUser.uid));
  if (!profileSnap.exists()) throw new Error('Your TELCOFAIR profile could not be found.');
  const profile = profileSnap.data() || {};
  if (String(profile.companyId || companyId) !== String(companyId)) throw new Error('Your account is not linked to this company.');
  const role = String(profile.role || profile.userRole || '').toLowerCase().replace(/[\s-]+/g, '_');
  const canManageCompanyPayment = Boolean(profile.isOwner) || ['admin', 'company_admin', 'owner'].includes(role);
  if (['company_registration', 'subscription', 'subscription_renewal', 'upgrade'].includes(paymentType) && !canManageCompanyPayment) {
    throw new Error('Only the company owner or company administrator can submit this payment.');
  }

  const asset = await uploadFile({
    companyId,
    scope: 'company',
    file: proofFile,
    folder: 'payment-proofs',
    allowedTypes: proofTypes,
    maxBytes: 10 * 1024 * 1024,
  });

  const payload = {
    source: 'manual',
    submissionChannel: 'company_portal',
    paymentType,
    companyId,
    companyName: companyName || company.companyName || company.name || '',
    userId: firebaseUser.uid,
    userName: userName || profile.displayName || `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || firebaseUser.displayName || '',
    email: String(email || profile.email || firebaseUser.email || '').trim().toLowerCase(),
    packageId,
    packageName: verifiedPlan.packageName || packageName,
    billingCycle: verifiedPlan.billingCycle || billingCycle,
    periodEnd,
    amount: expectedAmount,
    claimedAmount: Number(amount || expectedAmount),
    currency: verifiedPlan.currency || String(currency || options.currency || 'EUR').toUpperCase(),
    paymentMethod: method,
    transactionReference: reference,
    transactionReferenceNormalized: reference.toLowerCase(),
    paymentDate: paymentDate || new Date().toISOString().slice(0, 10),
    notes: String(notes || '').trim().slice(0, 2000),
    proofUrl: asset?.url || '',
    proofAsset: asset || null,
    status: 'pending_verification',
    reviewStatus: 'pending',
    rejectionReason: '',
    submittedBy: firebaseUser.uid,
    submittedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    businessStateApplied: false,
    ...(canResubmit ? {
      previousRejectionReason: String(existingData?.rejectionReason || ''),
      resubmittedAt: serverTimestamp(),
      resubmissionCount: Number(existingData?.resubmissionCount || 0) + 1,
    } : {}),
  };

  await runTransaction(db, async (transaction) => {
    const duplicate = await transaction.get(paymentRef);
    if (duplicate.exists()) {
      const duplicateData = duplicate.data() || {};
      const validResubmission = String(duplicateData.status || '').toLowerCase() === 'rejected'
        && String(duplicateData.companyId || '') === String(companyId)
        && String(duplicateData.userId || '') === String(firebaseUser.uid);
      if (!validResubmission) throw new Error('This transaction/reference number has already been submitted.');
      transaction.set(paymentRef, { ...payload, createdAt: duplicateData.createdAt || serverTimestamp() }, { merge: true });
    } else {
      transaction.set(paymentRef, payload);
    }
    const companyPatch = paymentType === 'company_registration' ? {
      paymentStatus: 'pending_verification',
      currentPaymentProofId: paymentId,
      registrationPaymentStatus: 'pending_verification',
      subscription: {
        ...(company.subscription || {}),
        status: 'pending_payment_review',
        pendingPaymentId: paymentId,
        pendingPaymentStatus: 'pending_verification',
      },
      updatedAt: serverTimestamp(),
    } : {
      currentPaymentProofId: paymentId,
      subscription: {
        ...(company.subscription || {}),
        pendingPaymentId: paymentId,
        pendingPaymentStatus: 'pending_verification',
      },
      updatedAt: serverTimestamp(),
    };
    transaction.set(doc(db, 'companies', companyId), companyPatch, { merge: true });
    transaction.set(doc(db, 'users', firebaseUser.uid), {
      paymentStatus: paymentType === 'company_registration' ? 'company_payment_pending_review' : profile.paymentStatus || '',
      registrationPaymentStatus: paymentType === 'company_registration' ? 'pending_verification' : profile.registrationPaymentStatus || '',
      registrationStatus: paymentType === 'company_registration' ? 'pending_company_subscription' : profile.registrationStatus || '',
      registrationIncomplete: paymentType === 'company_registration' ? true : profile.registrationIncomplete,
      registrationStage: paymentType === 'company_registration' ? 'company_subscription' : profile.registrationStage || '',
      stage: paymentType === 'company_registration' ? 'company_subscription' : profile.stage || '',
      currentPaymentProofId: paymentId,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });

  await setDoc(doc(collection(db, 'notifications')), {
    userId: 'all',
    type: 'payment_submitted',
    title: `${payload.companyName || 'A company'} submitted ${method === 'upi' ? 'UPI' : 'bank transfer'} payment`,
    body: `${payload.currency} ${payload.amount.toFixed(2)} · Ref ${reference}`,
    companyId,
    paymentId,
    link: '/payments/manual',
    readBy: [],
    createdAt: serverTimestamp(),
  }).catch(() => {});

  return { id: paymentId, ...payload, proofAsset: asset };
}

export async function listCompanyManualPayments(companyId) {
  if (!companyId) return [];
  const snap = await getDocs(query(collection(db, 'paymentProofs'), where('companyId', '==', companyId), limit(200)));
  return snap.docs.map((row) => ({ id: row.id, ...row.data() }))
    .filter((row) => row.source === 'manual')
    .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
}

export async function sendConfirmedOnlineReceipt({ companyId, payment = {}, email = '' }) {
  if (!companyId || !email) return { skipped: true };
  const runtime = await getPublicPlatformRuntime();
  const config = resolveReceiptConfig(runtime);
  const enriched = { ...payment, companyId, email, status: 'approved', paymentMethod: payment.paymentMethod || payment.provider || 'online_gateway' };
  const receiptNumber = receiptNumberFor(enriched, config);
  const receiptRef = doc(db, 'paymentReceipts', receiptNumber.replace(/[^a-zA-Z0-9_-]/g, '_'));
  const existing = await getDoc(receiptRef);
  if (existing.exists() && existing.data()?.emailSent === true) return { skipped: true, receiptNumber };
  const { attachment } = paymentReceiptAttachment({ ...enriched, receiptNumber }, config);
  const html = paymentReceiptEmailHtml({ ...enriched, receiptNumber }, config);
  try {
    await sendTransactionalEmail(companyId, {
      to: email,
      subject: `Payment approved · ${receiptNumber}`,
      html,
      text: `Your TELCOFAIR payment has been approved. Receipt ${receiptNumber}. Amount ${enriched.currency || 'EUR'} ${Number(enriched.amount || 0).toFixed(2)}.`,
      attachments: [attachment],
      operation: 'payment_approved_receipt',
      templateKey: 'payment_approved_receipt',
      variables: {
        firstName: String(enriched.userName || '').split(' ')[0] || 'Customer',
        fullName: enriched.userName || '',
        companyName: enriched.companyName || '',
        amount: `${enriched.currency || 'EUR'} ${Number(enriched.amount || 0).toFixed(2)}`,
        planName: enriched.packageName || enriched.planName || 'TELCOFAIR subscription',
        receiptNumber,
        transactionReference: enriched.transactionReference || enriched.providerReference || '',
        paymentMethod: String(enriched.paymentMethod || enriched.provider || 'online gateway').replace(/_/g, ' '),
        message: config.footer || 'Thank you for your payment.',
        platformName: 'TELCOFAIR',
      },
      receiptNumber,
    });
    await setDoc(receiptRef, { ...enriched, receiptNumber, emailSent: true, emailedAt: serverTimestamp(), createdAt: serverTimestamp() }, { merge: true });
    return { success: true, receiptNumber };
  } catch (error) {
    await setDoc(receiptRef, { ...enriched, receiptNumber, emailSent: false, emailError: String(error?.message || error), createdAt: serverTimestamp() }, { merge: true }).catch(() => {});
    return { success: false, receiptNumber, error };
  }
}

export async function reconcileApprovedOnlineRegistrationReceipt() {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser?.uid || !firebaseUser.email) return { skipped: true };
  const userSnap = await getDoc(doc(db, 'users', firebaseUser.uid));
  if (!userSnap.exists()) return { skipped: true };
  const user = userSnap.data() || {};
  const companyId = user.companyId || '';
  if (!companyId) return { skipped: true };
  const companySnap = await getDoc(doc(db, 'companies', companyId));
  if (!companySnap.exists()) return { skipped: true };
  const company = companySnap.data() || {};
  const subscription = company.subscription || {};
  const packageId = subscription.packageId || subscription.planId || user.registrationPackageId || '';
  let plan = {};
  if (packageId) {
    const planSnap = await getDoc(doc(db, 'subscriptionPlans', packageId));
    if (planSnap.exists()) plan = { id: planSnap.id, ...planSnap.data() };
  }
  const amount = Number(company.paymentAmount ?? subscription.price ?? plan.price ?? 0);
  if (!(amount > 0)) return { skipped: true };
  return sendConfirmedOnlineReceipt({
    companyId,
    email: firebaseUser.email,
    payment: {
      id: company.registrationPaymentId || user.registrationPaymentId || `online_registration_${firebaseUser.uid}_${packageId || 'plan'}`,
      companyName: company.companyName || company.name || '',
      userName: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || firebaseUser.email,
      paymentType: 'company_registration',
      packageId,
      packageName: subscription.packageName || plan.name || '',
      amount,
      currency: company.paymentCurrency || subscription.currency || plan.currency || 'EUR',
      provider: company.paymentProvider || user.paymentProvider || 'online_gateway',
      providerReference: company.registrationPaymentId || user.registrationPaymentId || '',
      status: 'approved',
    },
  });
}

export async function reconcileApprovedCompanySubscriptionReceipt(companyId) {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser?.uid || !firebaseUser.email || !companyId) return { skipped: true };
  const [userSnap, companySnap] = await Promise.all([
    getDoc(doc(db, 'users', firebaseUser.uid)),
    getDoc(doc(db, 'companies', companyId)),
  ]);
  if (!companySnap.exists()) return { skipped: true };
  const user = userSnap.exists() ? userSnap.data() || {} : {};
  if (user.companyId && String(user.companyId) !== String(companyId)) return { skipped: true };
  const company = companySnap.data() || {};
  const subscription = company.subscription || {};
  const packageId = subscription.packageId || subscription.planId || '';
  let plan = {};
  if (packageId) {
    const planSnap = await getDoc(doc(db, 'subscriptionPlans', packageId));
    if (planSnap.exists()) plan = { id: planSnap.id, ...planSnap.data() };
  }
  const amount = Number(company.paymentAmount ?? subscription.lastPaymentAmount ?? subscription.price ?? plan.price ?? 0);
  if (!(amount > 0)) return { skipped: true };
  const stableMarker = subscription.lastPaymentId || company.lastPaymentId || subscription.manualPaymentId
    || subscription.lastPaymentAt?.seconds || subscription.lastPaymentAt?._seconds
    || subscription.validUntil?.seconds || subscription.validUntil?._seconds || subscription.validUntil || 'current';
  return sendConfirmedOnlineReceipt({
    companyId,
    email: firebaseUser.email,
    payment: {
      id: subscription.lastPaymentId || company.lastPaymentId || `online_subscription_${companyId}_${packageId || 'plan'}_${stableMarker}`,
      companyName: company.companyName || company.name || '',
      userName: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || firebaseUser.email,
      paymentType: 'subscription',
      packageId,
      packageName: subscription.packageName || subscription.planName || plan.name || '',
      billingCycle: subscription.billingCycle || plan.billingCycle || '',
      amount,
      currency: company.paymentCurrency || subscription.currency || plan.currency || 'EUR',
      provider: company.paymentProvider || subscription.paymentProvider || 'online_gateway',
      providerReference: subscription.lastPaymentId || company.lastPaymentId || '',
      status: 'approved',
    },
  });
}
